import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import "react-day-picker/dist/style.css"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"

import { TooltipProvider } from "@/components/ui/tooltip"

import AppToaster from "./components/AppToaster"
import { ProtectedRoute } from "./components/ProtectedRoute"
import RoleTeamGuard from "./components/guards/RoleTeamGuard"
import { AuthProvider } from "./contexts/AuthContext"
import ActivateUser from "./pages/ActivateUser"
import ApiKeys from "./pages/ApiKeys"
import ApplicationCompare from "./pages/ApplicationCompare"
import ApplicationDetails from "./pages/ApplicationDetails"
import ApplicationDiffDebug from "./pages/ApplicationDiffDebug"
import ApplicationStageHistoryDebug from "./pages/ApplicationStageHistoryDebug"
import Applications from "./pages/Applications"
import AuditLogs from "./pages/AuditLogs"
import Collections from "./pages/Collections"
import DSP from "./pages/DSP"
import DVLAConsentAudit from "./pages/DVLAConsentAudit"
import DVLARecheck from "./pages/DVLARecheck"
import DriverManagerTeams from "./pages/DriverManagerTeams"
import ForgotPassword from "./pages/ForgotPassword"
import ImportDiaryEntries from "./pages/ImportDiaryEntries"
import IncentiveDetail from "./pages/IncentiveDetail"
import IncentiveReconcile from "./pages/IncentiveReconcile"
import Index from "./pages/Index"
import KeylessPortal from "./pages/KeylessPortal"
import Login from "./pages/Login"
import NoPermission from "./pages/NoPermission"
import NotFound from "./pages/NotFound"
import People from "./pages/People"
import PeopleActivityLogs from "./pages/PeopleActivityLogs"
import RolePermissions from "./pages/RolePermissions"
import SessionInvalidations from "./pages/SessionInvalidations"
import Sheriff from "./pages/Sheriff"
import StickyLocations from "./pages/StickyLocations"
import TeamPermissions from "./pages/TeamPermissions"
import UpdateContractStartDate from "./pages/UpdateContractStartDate"
import UserManagement from "./pages/UserManagement"
import Vehicles from "./pages/Vehicles"

const queryClient = new QueryClient()

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppToaster />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/activate-user" element={<ActivateUser />} />
            <Route path="/no-permission" element={<NoPermission />} />
            <Route path="/keyless" element={<KeylessPortal />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Index />} />
              <Route
                path="/applications"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "Manager", "DSP"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <Applications />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/people"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "DSP", "Manager"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <People />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/vehicles"
                element={
                  <RoleTeamGuard
                    permissions={["view.page.vehicles"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <Vehicles />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/incentives/new"
                element={
                  <RoleTeamGuard
                    permissions={["view.page.incentives"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <IncentiveDetail />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/incentives/reconcile"
                element={
                  <RoleTeamGuard
                    permissions={["view.page.incentives"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <IncentiveReconcile />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/incentives/:id"
                element={
                  <RoleTeamGuard
                    permissions={["view.page.incentives"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <IncentiveDetail />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/sheriff"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "Manager", "DSP"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <Sheriff />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/applications/compare"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "Manager", "DSP"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ApplicationCompare />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/applications/:id"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "Manager", "DSP"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ApplicationDetails />
                  </RoleTeamGuard>
                }
              />

              <Route
                path="/admin/people-activity-logs"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <PeopleActivityLogs />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/audit-logs"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <AuditLogs />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/import-diary-entries"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ImportDiaryEntries />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/application-stage-history"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ApplicationStageHistoryDebug />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/application-diff"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ApplicationDiffDebug />
                  </RoleTeamGuard>
                }
              />
              {/* Legacy redirects from /debug/* to /dev/* */}
              <Route
                path="/debug/application-stage-history"
                element={<Navigate to="/dev/application-stage-history" replace />}
              />
              <Route
                path="/debug/application-diff"
                element={<Navigate to="/dev/application-diff" replace />}
              />
              <Route
                path="/dev/role-permissions"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <RolePermissions />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/team-permissions"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <TeamPermissions />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/dsp"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <DSP />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/driver-manager-teams"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <DriverManagerTeams />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/sticky-locations"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <StickyLocations />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/dev/api-keys"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <ApiKeys />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/session-invalidations"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <SessionInvalidations />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/dvla-recheck"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <DVLARecheck />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/dvla-consent-audit"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <DVLAConsentAudit />
                  </RoleTeamGuard>
                }
              />
              {/* <Route path="/trial-blocks" element={<TrialBlocks />} />
              <Route path="/orientation" element={<Orientation />} />
              <Route path="/vehicle-booking" element={<VehicleBooking />} />
              <Route path="/metrics" element={<Metrics />} /> */}
              <Route
                path="/user-management"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <UserManagement />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/admin/update-contract-start-date"
                element={
                  <RoleTeamGuard
                    roles={["Admin"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <UpdateContractStartDate />
                  </RoleTeamGuard>
                }
              />
              <Route
                path="/collections"
                element={
                  <RoleTeamGuard
                    roles={["Admin", "Manager"]}
                    teams={["Central Operations"]}
                    fallback={<Navigate to="/no-permission" replace />}
                  >
                    <Collections />
                  </RoleTeamGuard>
                }
              />
              {/* Collections tooling moved to its own app - send old deep links to the notice */}
              <Route path="/collections/*" element={<Navigate to="/collections" replace />} />
            </Route>

            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
)

export default App
